import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { markSlideViewed, getActivityProgress } from '../utils/progressTracker';

const ACTIVITY_ID = 'bac-assp-bloc1';
const theme = { primary: '#8B5CF6', secondary: '#E75480', bg: '#FAF5FF', text: '#1a2e35' };
const titleImage = "https://static.prod-images.emergentagent.com/jobs/9345dd8f-d626-4299-a665-42cc7822d693/images/bd1395875212b134e35b22f9d0c21418c57eca4207d084f3bc0cd8ee36bda3f0.png";
const bgImage = "https://static.prod-images.emergentagent.com/jobs/9345dd8f-d626-4299-a665-42cc7822d693/images/1b86bb9c58ef07933a0550b45dc1c02e555bcaeb2e33b283aa07c8452fd5ab91.png";

const slides = [
  // ===== TITRE =====
  { type: 'title', image: titleImage },

  // ===== SOMMAIRE =====
  { type: 'sommaire', sections: [
    { num: "1", title: "Comprendre la personne accompagnée", target: 3 },
    { num: "2", title: "Relation et communication professionnelles", target: 46 },
    { num: "3", title: "Projet individualisé / projet de vie", target: 72 },
    { num: "4", title: "Accompagnement dans les actes de la vie quotidienne", target: 98 },
    { num: "5", title: "Activités de la vie quotidienne et de vie sociale", target: 114 },
    { num: "6", title: "Conception, conduite et évaluation d'une activité", target: 133 },
  ]},

  // =============================================
  // CHAPITRE 1 : Comprendre la personne accompagnée
  // =============================================
  { type: 'section', section: "1", title: "Comprendre la personne accompagnée" },

  // --- 1.1 ---
  { type: 'subsection', title: '1.1 Notion de personne, sujet de droits, dignité' },
  { type: 'richtext', title: 'Définition de la personne', blocks: [
    { text: "En ASSP, la personne n'est jamais réduite à sa maladie, son âge ou son handicap. C'est un sujet singulier, avec une histoire, des besoins, des désirs, des valeurs et des projets." },
  ]},
  { type: 'richtext', title: 'Sujet de droits', blocks: [
    { text: "La personne accompagnée est titulaire de droits fondamentaux : respect, liberté, intimité, vie privée, participation aux décisions qui la concernent.", list: [
      "Ces droits sont rappelés dans les textes (Déclaration des droits de l'homme, Code de l'action sociale et des familles, loi du 2 janvier 2002).",
      "L'accompagnement doit se faire avec la personne, et non à sa place, dès que possible.",
    ]},
  ]},
  { type: 'richtext', title: 'Dignité', blocks: [
    { text: "La dignité est inhérente à tout être humain, quel que soit son état de santé ou son autonomie.", sub: "La respecter, c'est :", list: [
      "préserver l'intimité (fermer la porte, couvrir le corps pendant les soins, demander l'accord avant de toucher) ;",
      "éviter les propos humiliants, infantilisants ou moqueurs ;",
      "parler à la personne, même quand elle ne répond pas ou semble désorientée.",
    ]},
  ]},

  // --- 1.2 ---
  { type: 'subsection', title: '1.2 Besoins fondamentaux (Virginia Henderson, Maslow)' },
  { type: 'richtext', title: 'Modèle de Virginia Henderson', blocks: [
    { text: "Virginia Henderson identifie 14 besoins fondamentaux (respirer, boire et manger, éliminer, se mouvoir, dormir, se vêtir, maintenir sa température, être propre, éviter les dangers, communiquer, agir selon ses valeurs, se réaliser, se recréer, apprendre).", sub: "Pour l'ASSP :", list: [
      "Observer dans quels besoins la personne a besoin d'aide.",
      "Adapter l'accompagnement pour soutenir au maximum ce qu'elle peut faire seule.",
    ], image: "https://customer-assets.emergentagent.com/job_c3dba0ea-cb7a-46e6-92bf-03d787efe83b/artifacts/87gbvlj8_image.png" },
  ]},
  { type: 'richtext', title: 'Pyramide de Maslow', blocks: [
    { text: "Maslow classe les besoins en cinq grandes catégories :", list: [
      "Besoins physiologiques (manger, dormir, respirer).",
      "Besoins de sécurité (logement, santé, environnement rassurant).",
      "Besoins d'appartenance (lien social, famille, amis, groupe).",
      "Besoins d'estime (reconnaissance, valorisation, confiance en soi).",
      "Besoins d'accomplissement (se réaliser, projets, créativité).",
    ], image: "https://customer-assets.emergentagent.com/job_c3dba0ea-cb7a-46e6-92bf-03d787efe83b/artifacts/syymk5f1_image.png" },
  ]},
  { type: 'richtext', title: "Le rôle de l'ASSP", blocks: [
    { heading: "L'ASSP doit :", list: [
      "S'assurer que les besoins vitaux et de sécurité sont couverts.",
      "Ne pas oublier les besoins d'amour, de reconnaissance et de réalisation, souvent négligés mais essentiels au bien-être.",
    ]},
  ]},

  // --- 1.3 ---
  { type: 'subsection', title: '1.3 Autonomie, dépendance, handicap, vulnérabilité' },
  { type: 'richtext', title: 'Autonomie', blocks: [
    { text: "Capacité d'une personne à :", list: [
      "décider pour elle-même ;",
      "réaliser les actes de la vie quotidienne.",
    ], note: "Il existe une autonomie physique, cognitive, psychique et sociale." },
  ]},
  { type: 'richtext', title: 'Dépendance', blocks: [
    { list: [
      "Besoin d'aide pour accomplir certains actes (toilette, déplacements, repas…).",
      "Elle peut être temporaire (fracture) ou durable (maladie chronique, grande vieillesse).",
      "L'objectif de l'ASSP est de maintenir ou restaurer ce qui peut l'être, et d'éviter l'aggravation.",
    ]},
  ]},
  { type: 'richtext', title: 'Handicap', blocks: [
    { text: "Selon la loi de 2005 : toute limitation d'activité ou restriction de participation à la vie en société, subie dans son environnement par une personne, en raison d'une altération substantielle, durable ou définitive d'une fonction.", note: "Types de handicap (exemples) : moteur, sensoriel, intellectuel, psychique, troubles du spectre de l'autisme, polyhandicap." },
  ]},
  { type: 'richtext', title: 'Vulnérabilité', blocks: [
    { list: [
      "Fragilité particulière qui peut être liée à l'âge, la maladie, la précarité, l'isolement, un handicap, une situation de violence…",
      "Impose une vigilance accrue : risque de maltraitance, de négligence, de non-recours aux droits.",
    ]},
  ]},

  // --- 1.4 ---
  { type: 'subsection', title: "1.4 Développement de l'enfant, de l'adolescent, de l'adulte et de la personne âgée" },
  { type: 'richtext', title: 'Enfant', blocks: [
    { list: [
      "0–3 ans : développement moteur (tenir sa tête, marcher), langage, attachement.",
      "3–6 ans : socialisation, jeux symboliques, autonomie progressive (habillage, repas).",
      "6–12 ans : acquisitions scolaires, règles sociales, développement de la pensée logique.",
    ]},
  ]},
  { type: 'richtext', title: 'Adolescent', blocks: [
    { list: [
      "Transformations physiques (puberté), quête d'identité, besoin de groupe, conflits possibles avec l'autorité.",
      "Risques : conduites à risque, repli, troubles du comportement, addictions.",
    ]},
  ]},
  { type: 'richtext', title: 'Adulte', blocks: [
    { list: [
      "Période de responsabilités (travail, famille).",
      "Peut être confronté à la maladie, au handicap, à la précarité, aux ruptures (séparation, perte d'emploi).",
    ]},
  ]},
  { type: 'richtext', title: 'Personne âgée', blocks: [
    { list: [
      "Vieillissement normal : ralentissement, diminution sensorielle, fatigabilité.",
      "Fragilités : chutes, isolement, dénutrition, troubles cognitifs possibles.",
      "Importance de préserver les repères, les habitudes et l'identité de la personne.",
    ]},
  ]},

  // --- 1.5 ---
  { type: 'subsection', title: '1.5 Facteurs influençant la santé et le bien-être' },
  { type: 'richtext', title: 'Les déterminants de santé', blocks: [
    { text: "Les déterminants de santé sont multiples et s'entrecroisent :", list: [
      "Facteurs biologiques : âge, sexe, patrimoine génétique, maladies, handicap.",
      "Facteurs psychologiques : estime de soi, capacité à gérer le stress, histoire personnelle.",
      "Facteurs sociaux : famille, réseau relationnel, niveau d'études, revenus, conditions de travail.",
      "Facteurs environnementaux : logement, pollution, bruit, climat, accessibilité des services.",
      "Facteurs comportementaux : alimentation, activité physique, consommation d'alcool, de tabac, d'écrans.",
    ], note: "L'ASSP doit avoir une vision globale : une personne n'est pas seulement « malade », elle vit dans un contexte qui peut aider ou freiner sa santé." },
  ]},

  // --- 1.6 ---
  { type: 'subsection', title: "1.6 Droits sociaux, prestations et financements" },
  { type: 'richtext', title: "Introduction", blocks: [
    { text: "L'accompagnement d'une personne ne se limite pas aux soins et à l'aide dans les actes de la vie quotidienne. Il implique également une compréhension globale de sa situation sociale, de ses droits et des dispositifs existants permettant de soutenir son autonomie, sa santé et sa qualité de vie." },
    { text: "Le professionnel du Bac Pro ASSP n'est pas chargé de monter les dossiers administratifs, mais il doit être capable d'identifier les besoins, de repérer les aides possibles et d'orienter la personne ou son entourage vers les bons interlocuteurs." },
  ]},
  { type: 'richtext', title: "Une compétence essentielle", blocks: [
    { text: "Cette compétence est essentielle pour assurer un accompagnement adapté, cohérent et réaliste, en tenant compte des ressources de la personne." },
  ]},

  // --- 1.6.1 ---
  { type: 'subsection', title: "1.6.1 Les grands principes de la protection sociale" },
  { type: 'richtext', title: "La protection sociale", blocks: [
    { text: "La protection sociale regroupe l'ensemble des dispositifs permettant de faire face aux risques de la vie : maladie, handicap, vieillesse, perte d'autonomie, précarité." },
    { text: "Elle repose sur plusieurs acteurs :", list: [
      "L'Assurance maladie",
      "Les départements (notamment pour l'APA et la PCH)",
      "La CAF",
      "La MDPH",
      "Les mutuelles et assurances complémentaires",
    ]},
    { note: "Ces dispositifs permettent de financer des soins, des aides humaines, du matériel, ou encore de compenser une perte de revenus." },
  ]},

  // --- 1.6.2 ---
  { type: 'subsection', title: "1.6.2 Les aides liées au handicap" },
  { type: 'richtext', title: "L'AAH (Allocation aux Adultes Handicapés)", blocks: [
    { text: "L'AAH est une aide financière destinée aux personnes en situation de handicap ayant des ressources limitées. Elle permet de garantir un minimum de revenus." },
    { text: "Elle est attribuée sous conditions :", list: [
      "Taux d'incapacité reconnu",
      "Âge (à partir de 20 ans, ou 16 ans sous conditions)",
      "Ressources",
    ]},
    { text: "Elle est versée par la CAF ou la MSA." },
  ]},
  { type: 'richtext', title: "La PCH (Prestation de Compensation du Handicap)", blocks: [
    { text: "La PCH est une aide essentielle pour compenser les conséquences du handicap dans la vie quotidienne. Elle peut financer :", list: [
      "Une aide humaine (aide à la toilette, aux repas, aux déplacements)",
      "Des aides techniques (fauteuil roulant, matériel médical)",
      "Des aménagements du logement ou du véhicule",
      "Des aides spécifiques ou exceptionnelles",
    ]},
    { text: "Elle est attribuée par le département après évaluation par la MDPH." },
    { note: "Le professionnel ASSP peut repérer qu'une personne a besoin d'aide dans les actes de la vie quotidienne et alerter l'équipe pour envisager une demande de PCH." },
  ]},

  // --- 1.6.3 ---
  { type: 'subsection', title: "1.6.3 Les aides liées à la perte d'autonomie" },
  { type: 'richtext', title: "L'APA (Allocation Personnalisée d'Autonomie)", blocks: [
    { text: "L'APA est destinée aux personnes âgées de 60 ans et plus en perte d'autonomie. Elle permet de financer :", list: [
      "De l'aide à domicile",
      "Du matériel",
      "Un accompagnement en établissement",
    ]},
  ]},
  { type: 'richtext', title: "La grille AGGIR et les GIR", blocks: [
    { text: "L'attribution dépend du niveau de dépendance évalué à partir de la grille AGGIR, qui classe les personnes en GIR (Groupe Iso-Ressources) :", list: [
      "GIR 1-2 : dépendance lourde",
      "GIR 3-4 : dépendance modérée",
      "GIR 5-6 : autonomie relative (non éligible à l'APA)",
    ]},
    { note: "Plus le GIR est faible (1), plus la dépendance est importante et plus les aides sont conséquentes. Le professionnel ASSP doit comprendre ces niveaux pour adapter son accompagnement et ses observations." },
  ]},

  // --- 1.6.4 ---
  { type: 'subsection', title: "1.6.4 La couverture santé" },
  { type: 'richtext', title: "Les dispositifs de couverture santé", blocks: [
    { heading: "L'Assurance maladie", text: "Elle prend en charge une partie des dépenses de santé (consultations, hospitalisation, médicaments)." },
    { heading: "La mutuelle (complémentaire santé)", text: "Elle complète les remboursements de l'Assurance maladie. Elle est essentielle pour limiter le reste à charge." },
    { heading: "La Complémentaire Santé Solidaire (CSS)", text: "Elle permet aux personnes ayant de faibles ressources de bénéficier d'une couverture santé gratuite ou à coût réduit." },
    { note: "Le professionnel peut repérer qu'une personne renonce aux soins pour des raisons financières et orienter vers une aide adaptée." },
  ]},

  // --- 1.6.5 ---
  { type: 'subsection', title: "1.6.5 Les aides au maintien à domicile" },
  { type: 'richtext', title: "Les dispositifs de maintien à domicile", blocks: [
    { text: "Le maintien à domicile est souvent privilégié, notamment chez les personnes âgées ou en situation de handicap. Plusieurs aides peuvent être mobilisées :", list: [
      "Services d'aide et d'accompagnement à domicile (SAAD)",
      "Portage de repas",
      "Téléassistance",
      "Aides techniques (barres d'appui, lit médicalisé)",
      "Aides financières (APA, PCH, aides des caisses de retraite)",
    ]},
    { note: "Le professionnel ASSP participe directement à ce maintien à domicile en assurant l'accompagnement quotidien." },
  ]},

  // --- 1.6.6 ---
  { type: 'subsection', title: "1.6.6 Les aides pour les enfants et les familles" },
  { type: 'richtext', title: "Aides aux familles", blocks: [
    { text: "Certaines aides concernent les familles et les enfants :", list: [
      "Prestations familiales (CAF)",
      "Aides pour enfants en situation de handicap (AEEH)",
      "Accompagnement précoce",
      "Dispositifs de protection de l'enfance",
    ]},
    { note: "En structure petite enfance ou à domicile, le professionnel peut repérer des besoins et orienter les familles." },
  ]},

  // --- 1.6.7 ---
  { type: 'subsection', title: "1.6.7 Le rôle du professionnel ASSP" },
  { type: 'richtext', title: "Repérage et orientation", blocks: [
    { text: "Le professionnel du Bac Pro ASSP n'est pas un travailleur social, mais il joue un rôle essentiel dans le repérage et l'orientation. Il doit être capable de :", list: [
      "Repérer une situation de précarité ou de difficulté",
      "Identifier un manque d'aide ou un besoin non couvert",
      "Transmettre les informations à l'équipe",
      "Orienter vers les professionnels compétents (assistant social, MDPH, CAF...)",
    ]},
    { note: "Il agit toujours dans le respect de la personne, de son consentement et de la confidentialité." },
  ]},

  // --- 1.6.8 ---
  { type: 'subsection', title: "1.6.8 Lien avec le projet individualisé" },
  { type: 'richtext', title: "Adapter le projet grâce aux aides", blocks: [
    { text: "La connaissance des aides et des financements permet d'adapter le projet individualisé :", list: [
      "Proposer des aides humaines adaptées",
      "Sécuriser le maintien à domicile",
      "Favoriser l'autonomie",
      "Éviter les ruptures de parcours",
    ]},
    { note: "Les aides ne sont pas une finalité, mais un moyen au service du projet de vie de la personne." },
  ]},

  { type: 'richtext', title: "Conclusion de la section 1.6", blocks: [
    { text: "Les aides sociales et les financements sont des leviers essentiels pour accompagner la personne dans le respect de ses besoins, de son autonomie et de son projet de vie." },
    { text: "Le professionnel ASSP, par sa proximité avec la personne, est en première ligne pour repérer les besoins et contribuer à une prise en charge adaptée et coordonnée." },
  ]},

  // =============================================
  // CHAPITRE 2 : Relation et communication professionnelles
  // =============================================
  { type: 'section', section: "2", title: "Relation et communication professionnelles" },

  // --- 2.1 ---
  { type: 'subsection', title: '2.1 Posture professionnelle : distance juste, empathie, neutralité bienveillante' },
  { type: 'richtext', title: 'Distance professionnelle', blocks: [
    { list: [
      "Ni fusion (trop proche), ni froideur (trop loin).",
      "Protéger la personne et le professionnel (éviter dépendance affective, épuisement).",
    ]},
  ]},
  { type: 'richtext', title: 'Empathie', blocks: [
    { list: [
      "Capacité à comprendre ce que ressent l'autre, sans se confondre avec lui.",
      "Se manifeste par l'écoute, le regard, la reformulation, la reconnaissance des émotions.",
    ]},
  ]},
  { type: 'richtext', title: 'Neutralité bienveillante', blocks: [
    { list: [
      "Ne pas juger les choix, les valeurs, le mode de vie de la personne.",
      "Garder une attitude respectueuse, même si on ne partage pas son avis.",
    ]},
  ]},

  // --- 2.2 ---
  { type: 'subsection', title: "2.2 Accueil de la personne et de son entourage" },
  { type: 'richtext', title: "Objectifs de l'accueil", blocks: [
    { list: [
      "Rassurer, informer, orienter.",
      "Donner une première image positive du service ou de l'établissement.",
    ]},
  ]},
  { type: 'richtext', title: 'Étapes clés', blocks: [
    { list: [
      "Préparation (connaître le dossier, le contexte d'arrivée).",
      "Premier contact (présentation, regard, sourire, disponibilité).",
      "Présentation des lieux, des règles et des interlocuteurs.",
      "Recueil des premières informations (habitudes, besoins urgents, inquiétudes).",
    ]},
  ]},
  { type: 'richtext', title: 'Attitudes attendues', blocks: [
    { list: [
      "Disponibilité, patience, respect du rythme de la personne.",
      "Attention à l'entourage (famille, aidants) : ils sont des partenaires essentiels.",
    ]},
  ]},

  // --- 2.3 ---
  { type: 'subsection', title: '2.3 Communication verbale, non verbale et para-verbale' },
  { type: 'richtext', title: 'Communication verbale', blocks: [
    { list: ["Mots choisis, phrases simples, vocabulaire adapté au public."] },
  ]},
  { type: 'richtext', title: 'Non-verbal', blocks: [
    { list: [
      "Regard, mimiques, posture, distance physique, gestes.",
      "Peut contredire ou renforcer le message verbal.",
    ]},
  ]},
  { type: 'richtext', title: 'Para-verbal', blocks: [
    { list: [
      "Ton de la voix, débit, volume, intonation.",
      "Un même mot peut rassurer ou agresser selon la manière de le dire.",
    ], note: "L'ASSP veille à la cohérence entre ce qu'il dit, ce qu'il montre et la façon dont il le dit." },
  ]},

  // --- 2.4 ---
  { type: 'subsection', title: "2.4 Écoute active, reformulation, entretien d'aide" },
  { type: 'richtext', title: 'Écoute active', blocks: [
    { list: [
      "Se rendre disponible : ne pas faire autre chose en même temps si possible.",
      "Montrer qu'on écoute (hochement de tête, « oui », regard).",
    ]},
  ]},
  { type: 'richtext', title: 'Reformulation', blocks: [
    { text: "Redire avec ses mots ce que la personne exprime :", list: [
      "« Si je comprends bien, vous… »",
      "Permet de vérifier la compréhension et montre à la personne qu'elle est entendue.",
    ]},
  ]},
  { type: 'richtext', title: "Entretien d'aide (niveau bac pro)", blocks: [
    { list: [
      "Objectif : permettre à la personne d'exprimer ses besoins, ses inquiétudes, ses projets.",
      "Limites : l'ASSP n'est ni psychologue ni thérapeute, il doit savoir orienter si nécessaire.",
    ]},
  ]},

  // --- 2.5 ---
  { type: 'subsection', title: '2.5 Communication avec des personnes présentant des troubles' },
  { type: 'richtext', title: 'Troubles sensoriels', blocks: [
    { list: [
      "Surdité : parler en face, articuler, utiliser l'écrit ou des gestes.",
      "Déficience visuelle : se présenter, expliquer les gestes, décrire l'environnement.",
    ]},
  ]},
  { type: 'richtext', title: 'Troubles cognitifs', blocks: [
    { text: "La maladie d'Alzheimer est la plus fréquente des démences. Elle se manifeste par les « 4 A » :", list: [
      "Amnésie : perte progressive de la mémoire (d'abord les faits récents, puis anciens).",
      "Aphasie : difficultés à trouver les mots, à comprendre le langage.",
      "Apraxie : incapacité à réaliser des gestes pourtant connus (habillage, utilisation d'objets).",
      "Agnosie : difficulté à reconnaître les visages, les objets, les lieux.",
    ], note: "Autres pathologies : démence à corps de Lewy, démence vasculaire, démence fronto-temporale, retard mental." },
  ]},
  { type: 'richtext', title: 'Adapter la communication', blocks: [
    { list: [
      "Utiliser des phrases courtes, un message à la fois.",
      "Se répéter calmement, s'appuyer sur les routines et les repères (photos, objets).",
    ]},
  ]},
  { type: 'richtext', title: 'Troubles du langage', blocks: [
    { list: [
      "Laisser le temps de s'exprimer, ne pas parler à la place de la personne trop vite.",
      "Utiliser supports visuels, pictogrammes si besoin.",
    ]},
  ]},

  // --- 2.6 ---
  { type: 'subsection', title: '2.6 Gestion de situations difficiles' },
  { type: 'richtext', title: 'Agressivité', blocks: [
    { list: [
      "Garder son calme, ne pas répondre par l'agressivité.",
      "Essayer de comprendre la cause (douleur, peur, incompréhension).",
      "Se protéger et protéger les autres, alerter l'équipe si nécessaire.",
    ]},
  ]},
  { type: 'richtext', title: 'Refus de soins', blocks: [
    { list: [
      "Chercher à comprendre le refus (douleur, pudeur, fatigue, peur).",
      "Expliquer le soin, négocier, proposer un autre moment ou une adaptation.",
      "Respecter la personne autant que possible et informer l'équipe.",
    ]},
  ]},
  { type: 'richtext', title: "Deuil, annonce d'un diagnostic", blocks: [
    { list: [
      "Rester présent, à l'écoute, sans paroles toutes faites.",
      "Éviter les phrases minimisantes ; préférer l'écoute silencieuse, la disponibilité.",
      "Savoir se tourner vers des professionnels formés (psychologue, médecin, cadre).",
    ]},
  ]},

  // =============================================
  // CHAPITRE 3 : Projet individualisé / projet de vie
  // =============================================
  { type: 'section', section: "3", title: "Projet individualisé / projet de vie" },

  // --- 3.1 ---
  { type: 'subsection', title: '3.1 Cadre légal : loi 2002-2, droits des usagers, participation' },
  { type: 'richtext', title: 'La loi du 2 janvier 2002', blocks: [
    { text: "La loi du 2 janvier 2002 a renforcé :", list: [
      "Les droits des usagers : respect, information, participation, recours.",
      "Le projet d'établissement et le projet individualisé.",
    ]},
  ]},
  { type: 'richtext', title: "Pierre angulaire de l'accompagnement", blocks: [
    { text: "C'est la pierre angulaire de l'accompagnement en structure médico-sociale, rendu obligatoire par la loi 2002-2." },
  ]},
  { type: 'richtext', title: 'En pratique', blocks: [
    { list: [
      "Chaque personne doit avoir un projet adapté à sa situation.",
      "La personne (et/ou sa famille) participe autant que possible à l'élaboration et au suivi du projet.",
    ]},
  ]},
  { type: 'richtext', title: 'Références légales', blocks: [
    { list: [
      "Loi 2002-2 du 2 janvier 2002 : Rénovant l'action sociale et médico-sociale. Elle place l'usager au centre du dispositif.",
      "Loi 2005-102 du 11 février 2005 : Pour l'égalité des droits et des chances des personnes handicapées.",
      "ASV du 28 décembre 2015 : Adaptation de la société au vieillissement.",
      "Loi du 5 mars 2007 : Protection des majeurs vulnérables (tutelle, curatelle).",
    ]},
  ]},

  // --- Les étapes ---
  { type: 'subsection', title: "Les étapes d'élaboration du projet individualisé" },
  { type: 'richtext', title: "Les étapes d'élaboration", blocks: [
    { list: [
      "1. Recueil des données : Histoire de vie, habitudes, souhaits (via le résident et sa famille).",
      "2. Évaluation des besoins : Utilisation de grilles comme AGGIR (autonomie), MNA (nutrition).",
      "3. Élaboration en équipe : Définition des objectifs (maintenir la marche, favoriser le lien social...).",
      "4. Mise en œuvre : Actions concrètes au quotidien.",
      "5. Évaluation et réajustement : Au moins une fois par an.",
    ]},
  ]},

  // --- 3.2 ---
  { type: 'subsection', title: '3.2 Recueil de données' },
  { type: 'richtext', title: 'Objectif', blocks: [
    { text: "Connaître la personne pour adapter l'accompagnement." },
  ]},
  { type: 'richtext', title: 'Données à recueillir (avec son accord)', blocks: [
    { list: [
      "Histoire de vie, parcours, métier, événements marquants.",
      "Habitudes (horaires, alimentation, hygiène, sommeil, loisirs, religion).",
      "Capacités et difficultés dans les actes de la vie quotidienne.",
      "Besoins exprimés, attentes, projets.",
    ]},
  ]},
  { type: 'richtext', title: 'Outils possibles', blocks: [
    { text: "Entretien, observation, dossier, échanges avec la famille et l'équipe." },
  ]},

  // --- 3.3 ---
  { type: 'subsection', title: '3.3 Analyse de la situation et besoins prioritaires' },
  { type: 'richtext', title: 'Repérer les problèmes et ressources', blocks: [
    { text: "À partir des données, l'équipe repère les problèmes et ressources." },
  ]},
  { type: 'richtext', title: 'Identifier les priorités', blocks: [
    { text: "Elle identifie ce qui est prioritaire pour le confort, la sécurité, l'autonomie et la qualité de vie." },
  ]},
  { type: 'richtext', title: 'Exemples de risques', blocks: [
    { list: ["Risque de chute, dénutrition, isolement social, perte de repères, épuisement de l'aidant."] },
  ]},

  // --- 3.4 ---
  { type: 'subsection', title: "3.4 Formulation des objectifs d'accompagnement" },
  { type: 'richtext', title: 'Un bon objectif doit être', blocks: [
    { list: [
      "Clair et précis.",
      "Réaliste et adapté à la personne.",
      "Évaluable (on doit pouvoir vérifier s'il est atteint).",
    ]},
  ]},
  { type: 'richtext', title: 'Exemples', blocks: [
    { list: [
      "« Maintenir la marche autonome sur 50 mètres avec déambulateur. »",
      "« Permettre une participation à un atelier cuisine par semaine. »",
    ]},
  ]},

  // --- 3.5 ---
  { type: 'subsection', title: '3.5 Élaboration, mise en œuvre et suivi' },
  { type: 'richtext', title: 'Planification', blocks: [
    { list: [
      "Définir qui fait quoi, quand et comment (planning, responsables, moyens).",
      "Mettre en œuvre au quotidien les actions prévues : soins, activités, aménagements.",
    ]},
  ]},
  { type: 'richtext', title: "Le rôle de l'ASSP", blocks: [
    { list: [
      "Observe.",
      "Réalise des actions d'accompagnement.",
      "Transmet les informations utiles.",
    ]},
  ]},

  // --- 3.6 ---
  { type: 'subsection', title: '3.6 Évaluation et réajustement' },
  { type: 'richtext', title: 'Évaluation régulière', blocks: [
    { text: "Le projet est évalué régulièrement (au moins une fois par an, et à chaque événement important)." },
  ]},
  { type: 'richtext', title: 'On observe', blocks: [
    { list: ["Objectifs atteints ?", "Besoins modifiés ?", "Nouveaux risques ?"] },
  ]},
  { type: 'richtext', title: 'Réajustement', blocks: [
    { text: "Le projet est réajusté : certains objectifs sont maintenus, d'autres modifiés ou remplacés." },
  ]},

  // =============================================
  // CHAPITRE 4 : Accompagnement dans les actes de la vie quotidienne
  // =============================================
  { type: 'section', section: "4", title: "Accompagnement dans les actes de la vie quotidienne" },

  // --- 4.1 ---
  { type: 'subsection', title: '4.1 Principes généraux' },
  { type: 'richtext', title: 'Respect', blocks: [
    { text: "Demander l'accord, expliquer, laisser le choix quand c'est possible." },
  ]},
  { type: 'richtext', title: 'Autonomie', blocks: [
    { text: "Laisser faire ce que la personne peut encore faire, même si cela prend plus de temps." },
  ]},
  { type: 'richtext', title: 'Sécurité', blocks: [
    { text: "Prévenir les chutes, les brûlures, les fausses routes, les infections." },
  ]},
  { type: 'richtext', title: 'Ergonomie', blocks: [
    { text: "Protéger la santé du professionnel (gestes et postures) et de la personne (installations adaptées)." },
  ]},

  // --- 4.2 ---
  { type: 'subsection', title: "4.2 Aide à l'habillage et au déshabillage" },
  { type: 'richtext', title: "Aide à l'habillage", blocks: [
    { list: [
      "Choisir des vêtements adaptés (température, pathologie, confort, image de soi).",
      "Expliquer les gestes, respecter la pudeur.",
      "Commencer par le côté valide pour l'habillage, par le côté atteint pour le déshabillage.",
      "Chez l'enfant : transformer l'habillage en moment d'apprentissage (laisser essayer, encourager).",
    ]},
  ]},

  // --- 4.3 ---
  { type: 'subsection', title: '4.3 Aide aux déplacements, transferts et installation' },
  { type: 'richtext', title: 'Aide aux déplacements', blocks: [
    { list: [
      "Aider la personne à se lever, s'asseoir, se coucher, changer de position.",
      "Utiliser les aides techniques (lève-personnes, déambulateur, fauteuil roulant) selon les consignes.",
      "Vérifier la sécurité : freins, repose-pieds, ceinture si nécessaire, absence d'obstacles au sol.",
    ]},
  ]},

  // --- 4.4 ---
  { type: 'subsection', title: '4.4 Aide au repos et au sommeil' },
  { type: 'richtext', title: 'Aide au repos', blocks: [
    { list: [
      "Respecter le rythme de la personne autant que possible.",
      "Favoriser un environnement calme, lumière adaptée, confort de la literie.",
      "Proposer des rituels rassurants (lecture, boisson chaude autorisée, discussion courte).",
    ]},
  ]},

  // --- 4.5 ---
  { type: 'subsection', title: "4.5 Aide à la prise des repas et à l'hydratation" },
  { type: 'richtext', title: 'Aide aux repas', blocks: [
    { list: [
      "Installer correctement : dos droit, tête légèrement fléchie, pieds posés au sol ou sur un support.",
      "Vérifier la texture des aliments (normes en cas de troubles de la déglutition).",
      "Donner à boire régulièrement, surveiller les quantités prises.",
      "Observer les signes de difficultés (fausses routes, refus, fatigue).",
    ]},
  ]},

  // --- 4.6 ---
  { type: 'subsection', title: "4.6 Accompagnement aux toilettes et à l'élimination" },
  { type: 'richtext', title: "Accompagnement à l'élimination", blocks: [
    { list: [
      "Préserver la pudeur (fermer la porte, couvrir la personne).",
      "Respecter les habitudes (horaires, préférences).",
      "Installer en sécurité (barres d'appui, sonnette à portée, hauteur adaptée).",
      "Surveiller les troubles éventuels (constipation, diarrhée, incontinence, douleurs).",
    ]},
  ]},

  // =============================================
  // CHAPITRE 5 : Activités de la vie quotidienne et de vie sociale
  // =============================================
  { type: 'section', section: "5", title: "Activités de la vie quotidienne et de vie sociale" },

  // --- 5.1 ---
  { type: 'subsection', title: '5.1 Rôle des activités' },
  { type: 'richtext', title: 'Rôle des activités', blocks: [
    { text: "Les activités ne sont pas un simple « occupationnel ». Elles permettent :", list: [
      "de maintenir ou restaurer des capacités (physiques, cognitives, sociales) ;",
      "de donner du sens et du plaisir au quotidien ;",
      "de lutter contre l'isolement et la perte d'estime de soi.",
    ]},
  ]},

  // --- 5.2 ---
  { type: 'subsection', title: '5.2 Activités de la vie quotidienne' },
  { type: 'richtext', title: 'Exemples', blocks: [
    { list: [
      "Participation aux repas : mise de table, épluchage simple, choix du menu, remplissage de la carafe.",
      "Entretien du cadre de vie : dépoussiérer, arroser les plantes, ranger, plier du linge.",
      "Gestion du temps : utiliser un calendrier, barrer les jours, noter les rendez-vous, repérer les fêtes.",
    ]},
  ]},
  { type: 'richtext', title: "L'objectif", blocks: [
    { text: "L'objectif est d'impliquer la personne, pas de faire « à sa place »." },
  ]},

  // --- 5.3 ---
  { type: 'subsection', title: "5.3 Activités d'autonomie" },
  { type: 'richtext', title: "Activités d'autonomie", blocks: [
    { list: [
      "Exercices de marche, utilisation d'aides techniques.",
      "Entraînement à certains gestes (boutonner, ouvrir une porte, couper les aliments).",
      "Organisation du quotidien (préparer un sac, gérer une liste de courses).",
    ]},
  ]},

  // --- 5.4 ---
  { type: 'subsection', title: '5.4 Activités de loisirs et de vie sociale' },
  { type: 'richtext', title: 'Culturelles / artistiques', blocks: [
    { text: "Lecture, musique, dessin, peinture, cinéma, spectacles." },
  ]},
  { type: 'richtext', title: 'Intergénérationnelles', blocks: [
    { text: "Rencontres enfants-personnes âgées, échanges de lectures, ateliers communs." },
  ]},
  { type: 'richtext', title: 'Sorties', blocks: [
    { text: "Marché, parc, musée, événements locaux, fêtes d'établissement." },
  ]},
  { type: 'richtext', title: 'Vie sociale', blocks: [
    { text: "Ces activités soutiennent l'appartenance à un groupe et la participation à la vie sociale." },
  ]},

  // --- 5.5 ---
  { type: 'subsection', title: "5.5 Activités d'éveil et de stimulation" },
  { type: 'richtext', title: 'Enfant', blocks: [
    { text: "Jeux d'éveil sensoriel, motricité, langage, jeux symboliques." },
  ]},
  { type: 'richtext', title: 'Personne âgée', blocks: [
    { text: "Ateliers mémoire, stimulation sensorielle (odeurs, textures, musique)." },
  ]},
  { type: 'richtext', title: 'Personne en situation de handicap', blocks: [
    { text: "Activités adaptées aux capacités (pictogrammes, matériel spécifique)." },
  ]},

  // --- 5.6 ---
  { type: 'subsection', title: '5.6 Prise en compte des goûts, croyances, cultures et rythmes' },
  { type: 'richtext', title: 'Goûts, croyances, cultures', blocks: [
    { list: [
      "Proposer, jamais imposer.",
      "Respecter les préférences (musique aimée, activités appréciées ou refusées).",
      "Tenir compte de la culture, de la religion, des fêtes importantes pour la personne.",
      "Adapter les horaires (matin/soir) selon les habitudes et la fatigue.",
    ]},
  ]},

  // =============================================
  // CHAPITRE 6 : Conception, conduite et évaluation d'une activité
  // =============================================
  { type: 'section', section: "6", title: "Conception, conduite et évaluation d'une activité" },

  // --- 6.1 ---
  { type: 'subsection', title: '6.1 Analyse de la situation et des besoins' },
  { type: 'richtext', title: 'Le diagnostic d\'activité', blocks: [
    { text: "Avant de proposer une activité, il faut répondre à :", list: [
      "Qui ? (profil de la personne ou du groupe)",
      "Pourquoi ? (besoins, difficultés, envies, objectifs)",
      "Quoi ? (type d'activité adaptée)",
    ]},
  ]},
  { type: 'richtext', title: 'Conclusion', blocks: [
    { text: "C'est le diagnostic d'activité." },
  ]},

  // --- 6.2 ---
  { type: 'subsection', title: "6.2 Objectifs d'activité" },
  { type: 'richtext', title: 'Les objectifs peuvent être', blocks: [
    { list: [
      "Cognitifs : mémoire, orientation, attention.",
      "Moteurs : équilibre, coordination, motricité fine ou globale.",
      "Sensoriels : vue, ouïe, toucher, odorat, goût.",
      "Sociaux : communication, coopération, participation au groupe.",
      "Émotionnels : plaisir, détente, expression des émotions, confiance.",
    ]},
  ]},
  { type: 'richtext', title: 'Critères', blocks: [
    { text: "Ils doivent être réalistes, adaptés au public et évaluables." },
  ]},

  // --- 6.3 ---
  { type: 'subsection', title: '6.3 Choix des supports, matériels et méthodes' },
  { type: 'richtext', title: 'Supports', blocks: [
    { text: "Jeux, images, objets, musique, vidéos, texte, matériel de cuisine, matériel artistique." },
  ]},
  { type: 'richtext', title: 'Méthodes', blocks: [
    { text: "Individuel, en binôme, en petit groupe, en atelier tournant." },
  ]},
  { type: 'richtext', title: 'Adaptation', blocks: [
    { text: "Tenir compte du niveau de compréhension, de mobilité, de fatigue." },
  ]},

  // --- 6.4 ---
  { type: 'subsection', title: "6.4 Préparation de l'espace, sécurité, conditions matérielles" },
  { type: 'richtext', title: 'Préparation', blocks: [
    { list: [
      "Organiser la salle : circulation, éclairage, bruit, confort.",
      "Vérifier la sécurité : chaises stables, sol dégagé, matériel non dangereux.",
      "Prévoir le matériel en quantité suffisante, testé et prêt à l'emploi.",
    ]},
  ]},

  // --- 6.5 ---
  { type: 'subsection', title: "6.5 Conduite de l'activité" },
  { type: 'richtext', title: "Conduite de l'activité", blocks: [
    { list: [
      "Accueillir les participants, se présenter, présenter l'activité.",
      "Expliquer les consignes de façon simple et progressive.",
      "Observer, encourager, adapter en fonction des réactions.",
      "Gérer le temps : durée adaptée, pauses si besoin, fin structurée (bilan, remerciements).",
    ]},
  ]},

  // --- 6.6 ---
  { type: 'subsection', title: '6.6 Observation, évaluation et transmissions' },
  { type: 'richtext', title: 'Observation et transmissions', blocks: [
    { list: [
      "Observer la participation, le plaisir, les difficultés, la fatigue, les interactions.",
      "Noter ce qui a bien fonctionné et ce qui est à améliorer.",
      "Transmettre à l'équipe (oralement et/ou par écrit) : effets de l'activité, points de vigilance, idées pour la suite.",
    ]},
  ]},

  // ===== FÉLICITATIONS =====
  { type: 'congratulations', title: "Félicitations !" },
];

const BACASSPBloc1Approche = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [viewedSlides, setViewedSlides] = useState(new Set([0]));
  const audioRef = useRef(null);
  const API_URL = window.location.origin || '';

  useEffect(() => {
    const saved = getActivityProgress(ACTIVITY_ID);
    if (saved?.viewedSlides) setViewedSlides(new Set(saved.viewedSlides));
  }, []);

  const speakText = async (text) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/tts/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: text.substring(0, 800), voice: 'nova', speed: 1.1 }) });
      if (r.ok) { const d = await r.json(); if (d.audio_url || d.audio_base64) { if (audioRef.current) audioRef.current.pause(); audioRef.current = new Audio(d.audio_url || `data:audio/mp3;base64,${d.audio_base64}`); audioRef.current.play(); setIsPlaying(true); audioRef.current.onended = () => setIsPlaying(false); } }
    } catch (e) { console.log('TTS unavailable'); }
    setIsLoading(false);
  };

  const stopAudio = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; setIsPlaying(false); } };

  const getSlideText = (s) => {
    let t = s.title || '';
    if (s.subtitle) t += ' ' + s.subtitle;
    if (s.blocks) s.blocks.forEach(b => { if (b.heading) t += ' ' + b.heading; if (b.text) t += ' ' + b.text; if (b.list) b.list.forEach(li => t += ' ' + li); });
    return t;
  };

  const nextSlide = () => { if (currentSlide < slides.length - 1) { const next = currentSlide + 1; setCurrentSlide(next); setViewedSlides(prev => new Set([...prev, next])); markSlideViewed(ACTIVITY_ID, next, slides.length); } };
  const prevSlide = () => { if (currentSlide > 0) { setCurrentSlide(currentSlide - 1); } };
  const goToSlide = (i) => { setCurrentSlide(i); setViewedSlides(prev => new Set([...prev, i])); markSlideViewed(ACTIVITY_ID, i, slides.length); };

  const progressPercent = Math.round((viewedSlides.size / slides.length) * 100);

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div className="flex items-center justify-center h-full" style={{ background: theme.bg }}>
            <img src={slide.image} alt="Bloc 1" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
        );

      case 'sommaire':
        return (
          <div className="h-full flex flex-col relative" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(250,245,255,0.78)' }} />
            <div className="relative z-10 h-full flex flex-col">
              <div style={{ background: `linear-gradient(135deg, ${theme.primary} 0%, #6D28D9 100%)`, padding: '0.6rem 0' }}>
                <h2 className="text-xl font-bold text-center text-white tracking-widest uppercase">Sommaire</h2>
              </div>
              <div className="flex-1 overflow-auto px-4 flex items-center justify-center">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-4xl mx-auto">
                  {slide.sections.map((s, idx) => (
                    <div key={idx}
                      className="flex items-center gap-3 p-3 bg-white/90 rounded-lg shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all backdrop-blur-sm"
                      style={{ border: '1px solid #e0e0e0' }}
                      onClick={() => goToSlide(s.target - 1)}>
                      <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 text-white" style={{ background: theme.primary }}>{s.num}</span>
                      <span className="text-gray-700 text-sm leading-tight font-medium">{s.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'section':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(139,92,246,0.45)' }} />
            <div className="text-center px-10 relative z-10">
              <span className="text-white/80 text-2xl md:text-3xl font-bold mb-3 block tracking-wide">Chapitre {slide.section}</span>
              <h2 className="text-xl md:text-2xl font-semibold text-white drop-shadow-lg">{slide.title}</h2>
            </div>
          </div>
        );

      case 'subsection':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(139,92,246,0.35)' }} />
            <div className="text-center px-10 relative z-10">
              <h2 className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg">{slide.title}</h2>
            </div>
          </div>
        );

      case 'richtext':
        return (
          <div className="h-full overflow-auto flex items-center justify-center p-6" style={{ background: theme.bg }}>
            <div className="max-w-3xl w-full">
              <h2 className="text-lg font-bold mb-4 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              <div className="space-y-3">
                {slide.blocks.map((block, i) => (
                  <div key={i} className="bg-white rounded-lg p-4 shadow-sm" style={{ border: `1px solid ${theme.primary}12` }}>
                    {block.heading && <h4 className="font-semibold mb-2 text-[15px]" style={{ color: theme.primary }}>{block.heading}</h4>}
                    {block.text && <p className="text-gray-700 text-[15px] leading-relaxed mb-2">{block.text}</p>}
                    {block.sub && <p className="text-gray-700 text-[15px] leading-relaxed font-medium mb-1.5">{block.sub}</p>}
                    {block.list && (
                      <div className="space-y-1.5">
                        {block.list.map((li, j) => (
                          <div key={j} className="flex items-start gap-2.5">
                            <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: theme.primary }} />
                            <span className="text-gray-600 text-[15px] leading-relaxed">{li}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {block.note && (
                      <p className="text-[14px] italic mt-2.5 p-2.5 rounded-md" style={{ background: `${theme.primary}08`, borderLeft: `3px solid ${theme.primary}`, color: '#475569' }}>{block.note}</p>
                    )}
                    {block.image && (
                      <div className="mt-3 text-center">
                        <img src={block.image} alt={block.heading || ''} className="max-w-full rounded-lg border border-gray-200 mx-auto" style={{ maxHeight: '180px', objectFit: 'contain' }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'congratulations':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(139,92,246,0.5)' }} />
            <div className="relative z-10 flex flex-col items-center text-center px-8 max-w-2xl">
              <div className="text-5xl mb-4">&#127942;</div>
              <h2 className="text-3xl font-bold text-white mb-4 drop-shadow-lg">{slide.title}</h2>
              <p className="text-white text-lg mb-3 leading-relaxed drop-shadow">
                Vous avez terminé la présentation du <strong>Bloc 1</strong> du BAC ASSP.
              </p>
              <p className="text-white text-base mb-6 leading-relaxed drop-shadow">
                Vous maîtrisez désormais les fondamentaux de l'accompagnement de la personne dans une approche globale et individualisée.
              </p>
              <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.9)' }}>
                <p className="text-base font-semibold mb-1" style={{ color: theme.primary }}>
                  Prêt(e) à tester vos connaissances ?
                </p>
                <p className="text-sm text-gray-600">
                  Rendez-vous dans le <strong>Quiz - Bloc 1</strong> pour évaluer votre maîtrise des concepts abordés dans ce cours.
                </p>
              </div>
            </div>
          </div>
        );

      default: return null;
    }
  };

  const slide = slides[currentSlide];
  return (
    <div className="rounded-xl overflow-hidden shadow-lg" style={{ background: theme.bg }} data-testid="bac-assp-bloc1-presentation">
      <div style={{ minHeight: '480px', height: '480px' }}>{renderSlide(slide)}</div>
      <div className="w-full h-2 bg-gray-200">
        <div className="h-full rounded-r-full transition-all duration-300" style={{ width: `${progressPercent}%`, background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})` }} />
      </div>
      <div className="flex items-center justify-between p-3 bg-white border-t" style={{ borderColor: theme.primary + '30' }}>
        <button onClick={prevSlide} disabled={currentSlide === 0} className="flex items-center gap-1 px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-40" style={{ borderColor: theme.primary, color: theme.text }} data-testid="bloc1-prev-btn">
          <ChevronLeft size={16} /> Précédent
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => isPlaying ? stopAudio() : speakText(getSlideText(slide))} className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ background: theme.primary }} data-testid="bloc1-audio-btn">
            {isLoading ? <Loader2 size={16} className="animate-spin" /> : isPlaying ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <span className="font-bold text-sm" style={{ color: theme.primary }}>{currentSlide + 1} / {slides.length}</span>
          <span className="text-xs text-gray-400">{progressPercent}%</span>
        </div>
        <button onClick={nextSlide} disabled={currentSlide === slides.length - 1} className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-40" style={{ background: theme.primary }} data-testid="bloc1-next-btn">
          Suivant <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default BACASSPBloc1Approche;
